import { useState, useEffect } from "react";
import { apiService, type AnalyticsFilterParams, type AnalyticsFiltersData, type AnalyticsSchoolsData } from "../../services/api.service";
import { Button } from "../ui/button";
import { Card, CardContent } from "../ui/card";

export interface FilterDisplayState {
    schoolId: string;
    schoolClassroomId: string;
    classroomId: string;
    lessonId: string;
    activePreset: string | null;
}

export const EMPTY_FILTER_DISPLAY: FilterDisplayState = {
    schoolId: "",
    schoolClassroomId: "",
    classroomId: "",
    lessonId: "",
    activePreset: null,
};

interface Props {
    filters: AnalyticsFilterParams;
    onApply: (filters: AnalyticsFilterParams) => void;
    showLessonFilter?: boolean;
    filterDisplay?: FilterDisplayState;
    onFilterDisplayChange?: (display: FilterDisplayState) => void;
}

const DATE_PRESETS = [
    { label: "7d", days: 7 },
    { label: "30d", days: 30 },
    { label: "90d", days: 90 },
    { label: "All", days: 0 },
];

function formatDate(d: Date): string {
    return d.toISOString().split("T")[0];
}

const inputClassName = "flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium file:text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50";

export const AnalyticsFilters = ({ filters, onApply, showLessonFilter = true, filterDisplay, onFilterDisplayChange }: Props) => {
    const [startDate, setStartDate] = useState(filters.start_date || "");
    const [endDate, setEndDate] = useState(filters.end_date || "");
    const [schoolId, setSchoolId] = useState<string>(filterDisplay?.schoolId ?? "");
    const [schoolClassroomId, setSchoolClassroomId] = useState<string>(filterDisplay?.schoolClassroomId ?? "");
    const [classroomId, setClassroomId] = useState<string>(filterDisplay?.classroomId ?? "");
    const [lessonId, setLessonId] = useState<string>(filterDisplay?.lessonId ?? "");
    const [loadError, setLoadError] = useState<string | null>(null);
    const [filterOptions, setFilterOptions] = useState<AnalyticsFiltersData | null>(null);
    const [schoolsData, setSchoolsData] = useState<AnalyticsSchoolsData | null>(null);
    const [activePreset, setActivePreset] = useState<string | null>(filterDisplay?.activePreset ?? null);

    useEffect(() => {
        apiService.getAnalyticsFilters().then(setFilterOptions).catch((error) => setLoadError(error instanceof Error ? error.message : "Failed to load filter options"));
        apiService.getAnalyticsSchools().then(setSchoolsData).catch((error) => setLoadError(error instanceof Error ? error.message : "Failed to load filter options"));
    }, []);

    useEffect(() => {
        setStartDate(filters.start_date || "");
        setEndDate(filters.end_date || "");
    }, [filters]);

    const handleApply = () => {
        setActivePreset(null);
        const f: AnalyticsFilterParams = {};
        if (startDate) f.start_date = startDate;
        if (endDate) f.end_date = endDate;

        if (schoolId && schoolId !== "all") {
            const school = schoolsData?.schools.find(s => s.id === schoolId);
            if (school) {
                if (schoolClassroomId) {
                    const classroom = school.classrooms.find(c => c.id === schoolClassroomId);
                    if (classroom) {
                        f.user_ids = classroom.user_ids;
                    }
                } else {
                    f.user_ids = school.classrooms.flatMap(c => c.user_ids);
                }
            }
        } else if (classroomId) {
            f.classroom_id = parseInt(classroomId);
        }

        if (showLessonFilter && lessonId) f.lesson_id = parseInt(lessonId);

        onFilterDisplayChange?.({ schoolId, schoolClassroomId, classroomId, lessonId, activePreset: null });
        onApply(f);
    };

    const handleClear = () => {
        setStartDate("");
        setEndDate("");
        setSchoolId("");
        setSchoolClassroomId("");
        setClassroomId("");
        setLessonId("");
        setActivePreset(null);
        onFilterDisplayChange?.(EMPTY_FILTER_DISPLAY);
        onApply({});
    };

    const handlePreset = (days: number, label: string) => {
        setActivePreset(label);
        onFilterDisplayChange?.({ schoolId, schoolClassroomId, classroomId, lessonId, activePreset: label });
        if (days === 0) {
            setStartDate("");
            setEndDate("");
        } else {
            const end = new Date();
            const start = new Date();
            start.setDate(end.getDate() - days);
            setStartDate(formatDate(start));
            setEndDate(formatDate(end));
        }
    };

    const handleSchoolChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        const newSchoolId = e.target.value;
        setSchoolId(newSchoolId);
        setSchoolClassroomId("");
        setClassroomId(null);
    };

    const classroomOptions = schoolId === "all" && filterOptions
        ? filterOptions.classrooms
        : [];

    const schoolClassroomOptions = schoolId && schoolId !== "all" && schoolsData
        ? schoolsData.schools.find(s => s.id === schoolId)?.classrooms.map(c => ({
              value: c.id,
              label: `${c.name} (${c.count} students)`
          })) || []
        : [];

    const lessonOptions = filterOptions
        ? filterOptions.lessons
        : [];

    return (
        <Card className="mb-6">
            {loadError && <p role="alert" className="p-3 text-sm text-destructive">{loadError}</p>}
            <CardContent className="p-4">
                {/* Row 1: Date presets + Date inputs */}
                <div className="flex items-end gap-3 mb-3">
                    <div className="flex gap-1">
                        {DATE_PRESETS.map((p) => (
                            <Button
                                key={p.label}
                                variant={activePreset === p.label ? "default" : "outline"}
                                size="sm"
                                className="h-9 px-3 text-xs"
                                onClick={() => handlePreset(p.days, p.label)}
                            >
                                {p.label}
                            </Button>
                        ))}
                    </div>
                    <div className="flex items-center gap-2">
                        <input
                            type="date"
                            className={inputClassName}
                            value={startDate}
                            onChange={(e) => { setStartDate(e.target.value); setActivePreset(null); }}
                        />
                        <span className="text-sm text-muted-foreground">to</span>
                        <input
                            type="date"
                            className={inputClassName}
                            value={endDate}
                            onChange={(e) => { setEndDate(e.target.value); setActivePreset(null); }}
                        />
                    </div>
                </div>

                {/* Row 2: School, Classroom, Lesson + Actions */}
                <div className="flex items-end gap-3">
                    <div className="flex-1 min-w-0">
                        <label className="text-xs font-medium text-muted-foreground mb-1 block">School</label>
                        <select
                            value={schoolId}
                            onChange={handleSchoolChange}
                            className={inputClassName}
                        >
                            <option value="">All</option>
                            {schoolsData?.schools.map(s => (
                                <option key={s.id} value={s.id}>{s.name}</option>
                            ))}
                            <option value="all">All (Select Classroom)</option>
                        </select>
                    </div>

                    <div className="flex-1 min-w-0">
                        <label className="text-xs font-medium text-muted-foreground mb-1 block">Classroom</label>
                        {schoolId && schoolId !== "all" ? (
                            <select
                                value={schoolClassroomId}
                                onChange={(e) => setSchoolClassroomId(e.target.value)}
                                className={inputClassName}
                            >
                                <option value="">All Rooms</option>
                                {schoolClassroomOptions.map(c => (
                                    <option key={c.value} value={c.value}>{c.label}</option>
                                ))}
                            </select>
                        ) : schoolId === "all" ? (
                            <select
                                value={classroomId}
                                onChange={(e) => setClassroomId(e.target.value)}
                                className={inputClassName}
                            >
                                <option value="">All Classrooms</option>
                                {classroomOptions.map(c => (
                                    <option key={c.id} value={c.id}>{c.name}</option>
                                ))}
                            </select>
                        ) : (
                            <select
                                disabled
                                className={inputClassName}
                            >
                                <option>Select school first</option>
                            </select>
                        )}
                    </div>

                    {showLessonFilter && (
                        <div className="flex-1 min-w-0">
                            <label className="text-xs font-medium text-muted-foreground mb-1 block">Lesson</label>
                            <select
                                value={lessonId}
                                onChange={(e) => setLessonId(e.target.value)}
                                className={inputClassName}
                            >
                                <option value="">All Lessons</option>
                                {lessonOptions.map(l => (
                                    <option key={l.id} value={l.id}>{l.name}</option>
                                ))}
                            </select>
                        </div>
                    )}

                    <div className="flex gap-2 shrink-0">
                        <Button onClick={handleApply} size="sm" className="h-9">
                            Apply
                        </Button>
                        <Button onClick={handleClear} variant="outline" size="sm" className="h-9">
                            Clear
                        </Button>
                    </div>
                </div>
            </CardContent>
        </Card>
    );
};
